import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Login from "./pages/Login";
import SuperAdminLayout from "./super-admin/SuperAdminLayout";
import {
  DashboardPage, UsersPage, JobsPage, RatingsPage, VerificationPage,
  CompliancePage, AnalyticsPage, IntegrationsPage, AuditPage, SystemPage, ProfilePage
} from "./super-admin/pages";
import ForgotPassword from "./pages/ForgotPassword";
import RegisterEmployee from "./pages/RegisterEmployee";
import ApplyForJob from "./pages/ApplyForJob";
import OpportunityHub from "./pages/OpportunityHub";
import RegisterEmployer from "./pages/RegisterEmployer";
import RegisterRecruiter from "./pages/RegisterRecruiter";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import JobSeekerDashboard from "./pages/JobSeekerDashboard";
import EmployerDashboard from "./pages/EmployerDashboard";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import CandidateProfile from "./pages/CandidateProfile";
import RateEmployer from "./pages/RateEmployer";
import RateEmployee from "./pages/RateEmployee";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PublicJobPage from "./pages/PublicJobPage";

/** Absolute session lifetime: 20 minutes from login (live PHP uses opaque tokens) */
const SESSION_LIMIT_MS = 20 * 60 * 1000;
const SESSION_STARTED_KEY = "wibil_session_started";

function clearAuthStorage() {
  sessionStorage.removeItem("wibil_token");
  sessionStorage.removeItem("wibil_user");
  sessionStorage.removeItem(SESSION_STARTED_KEY);
  localStorage.removeItem("wibil_token");
  localStorage.removeItem("wibil_user");
  localStorage.removeItem(SESSION_STARTED_KEY);
}

function getStoredToken() {
  return sessionStorage.getItem("wibil_token") || localStorage.getItem("wibil_token") || null;
}

function getSessionStartedAt() {
  const raw = sessionStorage.getItem(SESSION_STARTED_KEY) || localStorage.getItem(SESSION_STARTED_KEY);
  const started = Number(raw);
  return Number.isFinite(started) && started > 0 ? started : null;
}

function markSessionStarted(at = Date.now()) {
  const value = String(at);
  sessionStorage.setItem(SESSION_STARTED_KEY, value);
  localStorage.setItem(SESSION_STARTED_KEY, value);
  return at;
}

/** Real JWTs only (Node). Live PHP tokens are opaque strings. */
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === "function" ? atob(padded) : null;
    if (!json) return null;
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * Ensure a session start timestamp exists.
 * Prefer stored value, then JWT iat, else start the 20-min clock now (legacy live sessions).
 */
function ensureSessionStarted() {
  const existing = getSessionStartedAt();
  if (existing) return existing;
  const token = getStoredToken();
  const payload = decodeJwtPayload(token);
  if (payload && payload.iat) {
    return markSessionStarted(Number(payload.iat) * 1000);
  }
  return markSessionStarted(Date.now());
}

function isSessionExpired() {
  const token = getStoredToken();
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (payload && payload.exp && Date.now() >= Number(payload.exp) * 1000) return true;
  const started = getSessionStartedAt();
  if (!started) return false;
  return Date.now() - started >= SESSION_LIMIT_MS;
}

function loadSession() {
  const token = getStoredToken();
  const userStr = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
  if (!token || !userStr || userStr === "undefined") return null;
  const started = ensureSessionStarted();
  const payload = decodeJwtPayload(token);
  if ((payload && payload.exp && Date.now() >= Number(payload.exp) * 1000) || Date.now() - started >= SESSION_LIMIT_MS) {
    clearAuthStorage();
    return null;
  }
  try {
    const user = JSON.parse(userStr);
    if (!user || typeof user !== "object" || !user.role) {
      clearAuthStorage();
      return null;
    }
    return { token, user };
  } catch (e) {
    clearAuthStorage();
    return null;
  }
}

export default function App() {
  const [session, setSession] = useState(loadSession());
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem("wibil_theme_mode") || "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", themeMode);
    localStorage.setItem("wibil_theme_mode", themeMode);
  }, [themeMode]);

  const handleSetThemeMode = (mode) => {
    setThemeMode(mode);
  };

  const publicUrl = (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== ".") ? process.env.PUBLIC_URL.replace(/\/$/, "") : "";

  const onLogout = useCallback(() => {
    clearAuthStorage();
    setSession(null);
    window.location.href = `${publicUrl}/login`;
  }, [publicUrl]);

  const onAuth = ({ token, user }) => {
    sessionStorage.setItem("wibil_token", token);
    sessionStorage.setItem("wibil_user", JSON.stringify(user));
    localStorage.setItem("wibil_token", token);
    localStorage.setItem("wibil_user", JSON.stringify(user));
    markSessionStarted(Date.now());
    setSession({ token, user });
  };

  // Enforce absolute 20-minute session on live + local (timer + focus/visibility checks)
  useEffect(() => {
    if (!session) return undefined;

    const forceLogoutIfExpired = () => {
      ensureSessionStarted();
      if (isSessionExpired()) {
        onLogout();
        return true;
      }
      return false;
    };

    if (forceLogoutIfExpired()) return undefined;

    const started = getSessionStartedAt() || Date.now();
    const remaining = Math.max(0, SESSION_LIMIT_MS - (Date.now() - started));
    const timerId = window.setTimeout(() => onLogout(), remaining || 1);
    // Backup: browsers throttle background timers; re-check periodically and on focus
    const intervalId = window.setInterval(forceLogoutIfExpired, 15000);
    const onResume = () => { forceLogoutIfExpired(); };
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);

    return () => {
      window.clearTimeout(timerId);
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [session, onLogout]);

  const getActiveUser = () => {
    if (session && session.user) return session.user;
    const userStr = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
    if (userStr && userStr !== "undefined") {
      try {
        return JSON.parse(userStr);
      } catch (e) {}
    }
    return null;
  };

  const normalizeRole = (role) => {
    if (!role) return "";
    const r = String(role).toLowerCase().trim();
    if (r === "super_admin" || r === "super-admin" || r === "superadmin") return "super_admin";
    if (r === "job_seeker" || r === "jobseeker" || r === "job-seeker" || r === "candidate") return "job_seeker";
    if (r === "employer" || r === "company" || r === "corporate") return "employer";
    if (r === "recruiter" || r === "hr") return "recruiter";
    if (r === "employee" || r === "staff") return "employee";
    return r;
  };

  // Read user directly from storage (synchronous) — avoids stale React state during batched renders after navigate()
  const getUserFromStorage = () => {
    try {
      const userStr = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
      const token = getStoredToken();
      if (!token || !userStr || userStr === "undefined" || userStr === "null") return null;
      ensureSessionStarted();
      if (isSessionExpired()) {
        clearAuthStorage();
        return null;
      }
      const user = JSON.parse(userStr);
      if (!user || typeof user !== "object") return null;
      return user;
    } catch (e) { return null; }
  };

  const RootRedirect = () => {
    return <Navigate to="/login" replace />;
  };

  const AuthRoute = ({ element }) => {
    return element;
  };

  const requireAuth = (element) => {
    const user = getUserFromStorage();
    if (!user) return <Navigate to="/login" replace />;
    return element;
  };

  const requireRole = (role, element) => {
    const user = getUserFromStorage();
    if (!user || !user.role) {
      return <Navigate to="/login" replace />;
    }
    const userRole = normalizeRole(user.role);
    const targetRole = normalizeRole(role);
    // Strict role match: send mismatched roles to their own dashboard
    if (userRole && targetRole && userRole !== targetRole) {
      // Map role to its dashboard path
      const rolePathMap = { employee: "/employee", employer: "/employer", recruiter: "/recruiter", job_seeker: "/job-seeker", super_admin: "/super-admin" };
      const targetPath = rolePathMap[userRole] || "/login";
      return <Navigate to={targetPath} replace />;
    }
    return element;
  };

  return (
    <BrowserRouter basename={publicUrl}>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route 
          path="/login" 
          element={<Login onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />
        <Route 
          path="/login/:roleParam" 
          element={<Login onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />
        <Route 
          path="/forgot-password" 
          element={<ForgotPassword onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />
        <Route 
          path="/forgot-password/:roleParam" 
          element={<ForgotPassword onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />
        <Route 
          path="/register/employee" 
          element={<AuthRoute element={<RegisterEmployee onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} />} 
        />
        <Route 
          path="/register/job-seeker" 
          element={<AuthRoute element={<ApplyForJob onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} />} 
        />
        <Route 
          path="/register/employer" 
          element={<AuthRoute element={<RegisterEmployer onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} />} 
        />
        <Route 
          path="/register/recruiter" 
          element={<AuthRoute element={<RegisterRecruiter onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} />} 
        />

        <Route 
          path="/apply-for-job" 
          element={<ApplyForJob onAuth={onAuth} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />
        <Route 
          path="/opportunity-hub" 
          element={requireAuth((() => {
            const u = getUserFromStorage();
            const role = normalizeRole(u?.role);
            if (role === "job_seeker") return <Navigate to="/job-seeker" replace />;
            return <OpportunityHub user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />;
          })())} 
        />

        <Route 
          path="/employee" 
          element={requireAuth(<EmployeeDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/job-seeker" 
          element={requireAuth(<JobSeekerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/job-seeker-dashboard" 
          element={requireAuth(<JobSeekerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/job-seeker/:id" 
          element={requireAuth(<JobSeekerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/job_seeker" 
          element={requireAuth(<JobSeekerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/jobseeker" 
          element={requireAuth(<JobSeekerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/employee-profile" 
          element={requireAuth(<EmployeeDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/employee-profile/:id" 
          element={requireAuth(<EmployeeDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/employer" 
          element={requireAuth(<EmployerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/employer-dashboard" 
          element={requireAuth(<EmployerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/employer-profile" 
          element={requireAuth(<EmployerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/company-dashboard" 
          element={requireAuth(<EmployerDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/recruiter" 
          element={requireAuth(<RecruiterDashboard user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route
          path="/recruiter/candidate-profile"
          element={requireAuth(<CandidateProfile user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)}
        />
        <Route
          path="/recruiter/candidate-profile/:candidateId"
          element={requireAuth(<CandidateProfile user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)}
        />
        <Route
          path="/jobs/:slug"
          element={<PublicJobPage onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />}
        />
        <Route
          path="/linkedin-ads"
          element={requireAuth((() => {
            const u = getUserFromStorage();
            const role = normalizeRole(u?.role);
            if (role === "employer") return <Navigate to="/employer" replace />;
            return <Navigate to="/recruiter" replace />;
          })())}
        />

        <Route
          path="/super-admin"
          element={requireRole("super_admin", <SuperAdminLayout user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)}
        >
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage role="all" />} />
          <Route path="users/employees" element={<UsersPage role="employee" />} />
          <Route path="users/job-seekers" element={<UsersPage role="job_seeker" />} />
          <Route path="users/employers" element={<UsersPage role="employer" />} />
          <Route path="users/recruiters" element={<UsersPage role="recruiter" />} />
          <Route path="jobs" element={<JobsPage view="all" />} />
          <Route path="jobs/recruiter" element={<JobsPage view="recruiter" />} />
          <Route path="jobs/applications" element={<JobsPage view="applications" />} />
          <Route path="jobs/interviews" element={<JobsPage view="interviews" />} />
          <Route path="jobs/syndication" element={<JobsPage view="syndication" />} />
          <Route path="ratings/employees" element={<RatingsPage type="employee" />} />
          <Route path="ratings/employers" element={<RatingsPage type="employer" />} />
          <Route path="ratings/moderation" element={<RatingsPage type="employer" moderation />} />
          <Route path="verification/requests" element={<VerificationPage view="requests" />} />
          <Route path="verification/pending" element={<VerificationPage view="pending" />} />
          <Route path="verification/verified" element={<VerificationPage view="verified" />} />
          <Route path="verification/rejected" element={<VerificationPage view="rejected" />} />
          <Route path="compliance" element={<CompliancePage view="overview" />} />
          <Route path="compliance/consent" element={<CompliancePage view="consent" />} />
          <Route path="compliance/erasure" element={<CompliancePage view="erasure" />} />
          <Route path="analytics/employee-trust" element={<AnalyticsPage view="employee-trust" />} />
          <Route path="analytics/employer-trust" element={<AnalyticsPage view="employer-trust" />} />
          <Route path="analytics/platform" element={<AnalyticsPage view="platform" />} />
          <Route path="integrations/linkedin" element={<IntegrationsPage view="linkedin" />} />
          <Route path="integrations/indeed" element={<IntegrationsPage view="indeed" />} />
          <Route path="integrations/google" element={<IntegrationsPage view="google" />} />
          <Route path="integrations/signcare" element={<IntegrationsPage view="signcare" />} />
          <Route path="integrations/ai" element={<IntegrationsPage view="ai" />} />
          <Route path="integrations/health" element={<IntegrationsPage view="health" />} />
          <Route path="audit" element={<AuditPage view="audit" />} />
          <Route path="security/admin-activity" element={<AuditPage view="admin-activity" />} />
          <Route path="security/logins" element={<AuditPage view="logins" />} />
          <Route path="security/events" element={<AuditPage view="events" />} />
          <Route path="system" element={<SystemPage view="health" />} />
          <Route path="system/database" element={<SystemPage view="database" />} />
          <Route path="system/api" element={<SystemPage view="api" />} />
          <Route path="system/environment" element={<SystemPage view="environment" />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/security" element={<ProfilePage security />} />
        </Route>

        <Route 
          path="/rate/employer" 
          element={requireRole("employee", <RateEmployer user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />
        <Route 
          path="/rate/employee" 
          element={requireRole("employer", <RateEmployee user={session?.user} onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />)} 
        />

        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicy onLogout={onLogout} themeMode={themeMode} setThemeMode={handleSetThemeMode} />} 
        />

        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
